'use client'
import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";

import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import HomeIcon from "@mui/icons-material/Home";
import ForumIcon from "@mui/icons-material/Forum";
import SettingsIcon from "@mui/icons-material/Settings";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import Badge from "@mui/material/Badge";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";

import { useSelector, useDispatch } from "react-redux";

import { auth } from "config/firebase";
import { signOut } from "firebase/auth";
import Logo from "images/Logo.avif";
import { setUser, setUserToken } from "src/store/actions";
import { RootState } from "src/store";

const StyledNav = styled("nav")(({ theme }) => ({
  background: theme.palette.primary.main,
  position: "sticky",
  top: 0,
  width: "100%",
  zIndex: 100,
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
}));

const NavContainer = styled(Container)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1, 2),
  minHeight: 64,
}));

const NavbarSection = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));

const LogoLink = styled(NavLink)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  height: "100%",
  textDecoration: "none",
  "&:hover": {
    textDecoration: "none",
  },
  padding: theme.spacing(0, 1),
}));

const SiteName = styled(NavLink)(({ theme }) => ({
  textDecoration: "none",
  color: theme.palette.primary.contrastText,
  display: "flex",
  alignItems: "center",
  "&:hover": {
    textDecoration: "none",
    color: theme.palette.secondary.light,
  },
}));

const NavIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    color: theme.palette.secondary.light,
  },
  "&.active": {
    color: theme.palette.secondary.light,
    backgroundColor: "rgba(255, 255, 255, 0.05)",
  },
}));

const LogoutButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    color: theme.palette.error.light,
  },
}));

interface NavbarProps {
  msgsCount?: {
    numMsgs: number;
    unreadMsgs: number;
    unreadMsg?: boolean;
  } | null;
}

function NavbarComponent({ msgsCount }: NavbarProps) {
  const user = useSelector((state: RootState) => state.user.user);
  const claims = useSelector((state: RootState) => state.user.token);
  const requests = useSelector(
    (state: RootState) => state.requests.receivedRequests,
  );
  const requestsPending = useSelector(
    (state: RootState) => state.requests.sentRequests,
  );

  const [numUnread, setNumUnread] = useState<number>(0);

  useEffect(() => {
    if (msgsCount) {
      setNumUnread(msgsCount.unreadMsgs || 0);
    } else {
      setNumUnread(0);
    }
  }, [msgsCount]);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const logout = async (): Promise<void> => {
    try {
      await signOut(auth);
      // Auth listener will handle clearing Redux state
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
      // Fallback: clear local state if Firebase signOut fails
      dispatch(setUser(null));
      dispatch(setUserToken(null));
      navigate("/");
    }
  };

  const numRequests = (): number => {
    const keys = Object.keys(requests);
    const pending = keys.filter((key) => requests[key].status === "pending");
    return pending.length;
  };

  const pendingConfirmation = (): number => {
    const keys = Object.keys(requestsPending);
    const pending = keys.filter(
      (key) => requestsPending[key].status === "accepted",
    );
    return pending.length;
  };

  const renderHostLink = (): JSX.Element | null => {
    if (claims?.isHost) {
      return (
        <NavLink to="/hostpanel">
          {({ isActive }) => (
            <Tooltip title="Host Panel" placement="bottom">
              <NavIconButton className={isActive ? "active" : ""}>
                <Badge badgeContent={numRequests()} color="error">
                  <SupervisorAccountIcon />
                </Badge>
              </NavIconButton>
            </Tooltip>
          )}
        </NavLink>
      );
    }
    return null;
  };

  const renderUserLinks = (): JSX.Element | null => {
    if (user) {
      return (
        <>
          <NavLink to="/home">
            {({ isActive }) => (
              <Tooltip title="Home" placement="bottom">
                <NavIconButton className={isActive ? "active" : ""}>
                  <Badge badgeContent={pendingConfirmation()} color="error">
                    <HomeIcon />
                  </Badge>
                </NavIconButton>
              </Tooltip>
            )}
          </NavLink>

          <NavLink to="/messages">
            {({ isActive }) => (
              <Tooltip title="Messages" placement="bottom">
                <NavIconButton className={isActive ? "active" : ""}>
                  <Badge badgeContent={numUnread} max={99} color="error">
                    <ForumIcon />
                  </Badge>
                </NavIconButton>
              </Tooltip>
            )}
          </NavLink>

          <NavLink to="/settings">
            {({ isActive }) => (
              <Tooltip title="Settings" placement="bottom">
                <NavIconButton className={isActive ? "active" : ""}>
                  <SettingsIcon />
                </NavIconButton>
              </Tooltip>
            )}
          </NavLink>

          <Tooltip title="Log Out" placement="bottom">
            <LogoutButton onClick={logout}>
              <ExitToAppIcon />
            </LogoutButton>
          </Tooltip>
        </>
      );
    }
    return null;
  };

  return (
    <StyledNav>
      <NavContainer maxWidth="xl">
        <NavbarSection>
          <LogoLink to={user ? "/home" : "/"}>
            <Box
              component="img"
              src={Logo}
              alt="Logo"
              sx={{
                width: 40,
                height: 40,
                objectFit: "contain",
              }}
            />
          </LogoLink>

          <SiteName to={user ? "/home" : "/"}>
            <Typography variant="h6" component="span" sx={{ fontWeight: 500 }}>
              Local Poker
            </Typography>
          </SiteName>

          <NavLink to="/info">
            {({ isActive }) => (
              <Tooltip title="About Local Poker" placement="bottom">
                <NavIconButton className={isActive ? "active" : ""}>
                  <InfoRoundedIcon />
                </NavIconButton>
              </Tooltip>
            )}
          </NavLink>
        </NavbarSection>

        <NavbarSection>
          {renderHostLink()}
          {renderUserLinks()}
        </NavbarSection>
      </NavContainer>
    </StyledNav>
  );
}

export default NavbarComponent;
